import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, makeStyles, MenuItem, Select, InputLabel, FormControl, Button, TextField, IconButton } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

import type { RootState } from '../../store/store';
import {
	name as channelFacetSelector,
	setIdFacet,
	setNameFacet,
	setTerritoryFacet,
	setTypeFacet,
	toggleHighlightsFacet,
	resetAllFacets
} from '../../store/reducers/channelFacet';

const useStyles = makeStyles((theme) => ({
	filter: {
		marginBottom: theme.spacing(2)
	},
	text: {
		width: 230
	},
	select: {
		width: 150
	},
	facetButton: {
		height: '2.5rem',
		'& .MuiSvgIcon-root': {
			marginRight: 5
		}
	}
}));

const ChannelsFilters: FC = () => {
	const classes = useStyles();
	const dispatch = useDispatch();

	// All these search facets come from the channelFacetSelector in the redux store
	const { highlights, id, name, territory, type } = useSelector((state: RootState) => state[channelFacetSelector]);

	const hasFacets = id || name || territory || type || highlights === true;

	return (
		<Grid container spacing={1} className={classes.filter} alignItems='center'>
			<Grid item>
				<TextField
					size='small'
					data-testid='channels-filters-id'
					id='id'
					label='Id'
					name='id'
					value={id || ''}
					onChange={({ target: { value } }): object => dispatch(setIdFacet(value || null))}
					variant='outlined'
					className={classes.text}
					InputProps={{
						endAdornment: id ? (
							<IconButton size='small' onClick={(): object => dispatch(setIdFacet(null))}>
								<ClearIcon />
							</IconButton>
						) : undefined
					}}
				/>
			</Grid>

			<Grid item>
				<TextField
					size='small'
					data-testid='channels-filters-name'
					id='name'
					label='Name'
					name='name'
					value={name || ''}
					onChange={({ target: { value } }): object => dispatch(setNameFacet(value || null))}
					variant='outlined'
					className={classes.text}
					InputProps={{
						endAdornment: name ? (
							<IconButton size='small' onClick={(): object => dispatch(setNameFacet(null))}>
								<ClearIcon />
							</IconButton>
						) : undefined
					}}
				/>
			</Grid>

			<Grid item>
				<FormControl variant='outlined' size='small'>
					<InputLabel id='territory-label'>Territory</InputLabel>
					<Select
						labelId='territory-label'
						id='territory'
						data-testid='channels-filters-territory'
						label='TERRITORY'
						name='territory'
						value={territory || ''}
						onChange={({ target: { value } }): object => dispatch(setTerritoryFacet(value || null))}
						className={classes.select}>
						<MenuItem value=''>
							<em>None</em>
						</MenuItem>
						<MenuItem value='AT'>Austria</MenuItem>
						<MenuItem value='DE'>Germany</MenuItem>
						<MenuItem value='GB'>Great Britain</MenuItem>
						<MenuItem value='IT'>Italy</MenuItem>
						<MenuItem value='US'>United States</MenuItem>
					</Select>
				</FormControl>
			</Grid>

			<Grid item>
				<FormControl size='small' variant='outlined'>
					<InputLabel id='type-label'>Type</InputLabel>
					<Select
						labelId='type-label'
						id='type'
						data-testid='channels-filters-type'
						label='TYPE'
						name='type'
						value={type || ''}
						onChange={({ target: { value } }): object => dispatch(setTypeFacet(value || null))}
						className={classes.select}>
						<MenuItem value='' key={'none'}>
							<em>None</em>
						</MenuItem>
						<MenuItem value='EXTERNAL'>External</MenuItem>
						<MenuItem value='LIVE'>Live</MenuItem>
						<MenuItem value='TEST'>Test</MenuItem>
					</Select>
				</FormControl>
			</Grid>

			<Grid item>
				<Button
					data-testid='channels-filters-highlights'
					variant={highlights ? 'contained' : 'outlined'}
					color={highlights ? 'primary' : undefined}
					disableElevation
					onClick={(): object => dispatch(toggleHighlightsFacet())}
					className={classes.facetButton}>
					{highlights && <CheckBoxIcon fontSize='medium' />}
					{!highlights && <CheckBoxOutlineBlankIcon fontSize='medium' />}
					Highlights
				</Button>
			</Grid>

			{hasFacets && (
				<Grid item>
					<Button
						data-testid='channels-filters-clearfilters'
						variant='outlined'
						color='default'
						onClick={(): object => dispatch(resetAllFacets())}
						className={classes.facetButton}>
						<ClearIcon fontSize='medium' />
						Clear filters
					</Button>
				</Grid>
			)}
		</Grid>
	);
};

export default ChannelsFilters;
